import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import type {
  IRowNode,
  RowClassParams,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useAccessToken } from '@/services/auth';
import { PrimaryContext } from '@/services/context';
import {
  isFlushEmbryoActionable,
  isOnFarmFreezingSelectionEligible,
  type OnFarmImplantSessionEmbryo,
  onFarmImplantSessionEmbryosOptions,
} from '@/services/on-farm-implant';
import { useContextMenu } from '@/utils/context-menu';

import { SendToFreezingDialog } from '../OnFarmFreezingTab/SendToFreezingDialog';
import { useFlushEmbryoColumnDefs } from './columnDefs';
import {
  type FlushConfirmAction,
  FlushEmbryoActionDialog,
} from './FlushEmbryoActionDialog';

type FlushTabProps = {
  sessionId: string | null;
  readOnly?: boolean;
};

type FlushAction = FlushConfirmAction | 'freezing';

export const FlushTab = ({ sessionId, readOnly = false }: FlushTabProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'embryoId' });
  const gridRef = useRef<AgGridReact>(null);

  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openAction, setOpenAction] = useState<FlushAction | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    ...onFarmImplantSessionEmbryosOptions(token, {
      sessionId: sessionId ?? '',
      sourceType: 'flush',
    }),
    enabled: !!token && !!sessionId,
  });

  const rows = useMemo(() => data?.items ?? [], [data]);

  const selectableCount = useMemo(
    () => rows.filter(isFlushEmbryoActionable).length,
    [rows],
  );

  const selectedEmbryos = useMemo(() => {
    const ids = new Set(selectedIds);
    return rows.filter((row) => ids.has(row.sessionEmbryoId));
  }, [rows, selectedIds]);

  const handleDialogOpenChange = useCallback((open: boolean) => {
    if (!open) setOpenAction(null);
  }, []);

  const columnDefs = useFlushEmbryoColumnDefs(readOnly);

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    setSelectedIds(
      (event.api.getSelectedRows() as OnFarmImplantSessionEmbryo[]).map(
        (row) => row.sessionEmbryoId,
      ),
    );
  }, []);

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api.deselectAll();
  }, []);

  const handleActionDone = useCallback(() => {
    gridRef.current?.api.deselectAll();
  }, []);

  const isRowSelectable = useCallback(
    (node: IRowNode) =>
      !readOnly &&
      !!node.data &&
      isFlushEmbryoActionable(node.data as OnFarmImplantSessionEmbryo),
    [readOnly],
  );

  const getRowClass = useCallback(
    (params: RowClassParams) =>
      params.data &&
      !isFlushEmbryoActionable(params.data as OnFarmImplantSessionEmbryo)
        ? 'opacity-60'
        : undefined,
    [],
  );

  if (!sessionId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-list mb-3 text-4xl" />
        <p className="text-sm">{t('onFarmImplant.flush.noSession')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner text-sm" />
        {t('onFarmImplant.flush.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-gray-500">
        <i className="pi pi-exclamation-triangle text-3xl text-amber-500" />
        <p className="text-sm">{t('onFarmImplant.flush.loadError')}</p>
        <Button
          onClick={() => {
            void refetch();
          }}
        >
          {t('onFarmImplant.flush.retry')}
        </Button>
      </div>
    );
  }

  if (!data?.sourceSelected) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-list mb-3 text-4xl" />
        <p className="text-base font-medium">
          {t('onFarmImplant.flush.notLoadedTitle')}
        </p>
        <p className="mt-1 text-sm">{t('onFarmImplant.flush.notLoadedBody')}</p>
      </div>
    );
  }

  const hasSelection = selectedEmbryos.length > 0;

  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative w-56">
            <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
            <Input
              placeholder={t('onFarmImplant.flush.filterPlaceholder')}
              aria-label={t('onFarmImplant.flush.filterPlaceholder')}
              className="h-7 pl-7 text-xs"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.flush.embryoCount', {
              count: data.totalRecords,
            })}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.flush.selectedCount', {
              selected: selectedEmbryos.length,
              total: selectableCount,
            })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasSelection}
            onClick={handleClearSelection}
          >
            {t('onFarmImplant.flush.clearSelection')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={readOnly || !hasSelection}
            onClick={() => setOpenAction('discard')}
          >
            {t('onFarmImplant.flush.discard.action')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={
              readOnly || !isOnFarmFreezingSelectionEligible(selectedEmbryos)
            }
            title={t('onFarmImplant.onFarmFreezing.send.ineligibleHint')}
            onClick={() => setOpenAction('freezing')}
          >
            {t('onFarmImplant.onFarmFreezing.send.button')}
          </Button>
          <Button
            size="sm"
            disabled={readOnly || !hasSelection}
            onClick={() => setOpenAction('queue')}
          >
            {t('onFarmImplant.flush.queue.addToQueue')}
          </Button>
        </div>
      </div>

      <div
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-13rem)] min-h-[400px] w-full`}
      >
        <WithLocalStorage storageKey="on-farm-implant-flush-tab">
          <AgGridReact
            ref={gridRef}
            gridOptions={gridOptions}
            columnDefs={columnDefs as AgGridReactProps['columnDefs']}
            defaultColDef={defaultColDef}
            rowData={rows}
            headerHeight={36}
            rowHeight={48}
            rowSelection="multiple"
            suppressRowClickSelection
            isRowSelectable={isRowSelectable}
            getRowClass={getRowClass}
            quickFilterText={searchText}
            overlayNoRowsTemplate={`<span>${
              searchText
                ? t('onFarmImplant.flush.noMatches')
                : t('onFarmImplant.flush.noEmbryos')
            }</span>`}
            getRowId={getRowId<OnFarmImplantSessionEmbryo>(
              (params) => params.data.sessionEmbryoId,
            )}
            getContextMenuItems={getContextMenuItems}
            onSelectionChanged={handleSelectionChanged}
          />
        </WithLocalStorage>
      </div>

      {(openAction === 'queue' || openAction === 'discard') && (
        <FlushEmbryoActionDialog
          action={openAction}
          open
          onOpenChange={handleDialogOpenChange}
          sessionId={sessionId}
          embryos={selectedEmbryos}
          onDone={handleActionDone}
        />
      )}

      <SendToFreezingDialog
        open={openAction === 'freezing'}
        onOpenChange={handleDialogOpenChange}
        sessionId={sessionId}
        embryos={selectedEmbryos.map((embryo) => ({
          sessionEmbryoId: embryo.sessionEmbryoId,
          embryoName: embryo.embryoName,
          strawNumber: embryo.frozenStrawNumber,
          stage: embryo.stage,
          grade: embryo.grade,
          versions: embryo.versions,
        }))}
        onSent={handleActionDone}
      />
    </div>
  );
};
